import type {Schema} from '@portabletext/schema'
import {
  isTextBlock,
  type PortableTextBlock,
  type PortableTextObject,
} from '@portabletext/schema'
import {vercelStegaClean} from '@vercel/stega'
import type {
  ArbitraryTypedObject,
  DeserializerRule,
  HtmlParser,
  PlaceholderAnnotation,
  PlaceholderDecorator,
  TypedObject,
} from '../types'
import {flattenNestedBlocks} from './flatten-nested-blocks'
import {
  defaultParseHtml,
  ensureRootIsBlocks,
  isMinimalBlock,
  isMinimalSpan,
  isNodeList,
  isPlaceholderAnnotation,
  isPlaceholderDecorator,
  tagName,
} from './helpers'
import {preprocessors} from './preprocessors'
import {keyGenerator} from './random-key'
import {resolveJsType} from './resolve-js-type'
import {createRules} from './rules'
import type {SchemaMatchers} from './schema-matchers'
import {trimWhitespace} from './trim-whitespace'

interface HtmlDeserializerOptions {
  keyGenerator?: () => string
  rules?: DeserializerRule[]
  parseHtml?: HtmlParser
  whitespaceMode?: 'preserve' | 'remove' | 'normalize'
  matchers?: SchemaMatchers
}

/**
 * HTML Deserializer
 *
 */
export class HtmlDeserializer {
  keyGenerator: () => string
  schema: Schema
  rules: DeserializerRule[]
  parseHtml: (html: string) => HTMLElement
  whitespaceMode: 'preserve' | 'remove' | 'normalize'
  _markDefs: PortableTextObject[] = []

  /**
   * Create a new serializer respecting a Sanity block content type's schema
   *
   * @param schema - Schema definition
   * @param options - Options for the deserialization process
   */
  constructor(schema: Schema, options: HtmlDeserializerOptions = {}) {
    const {rules = [], whitespaceMode = 'preserve'} = options
    const standardRules = createRules(schema, {
      keyGenerator: options.keyGenerator,
      matchers: options.matchers,
    })
    this.schema = schema
    this.keyGenerator = options.keyGenerator ?? keyGenerator
    this.rules = [...rules, ...standardRules]
    this.whitespaceMode = whitespaceMode
    const parseHtml = options.parseHtml || defaultParseHtml()
    this.parseHtml = (html) => {
      const cleanHTML = vercelStegaClean(html)
      const doc = parseHtml(cleanHTML)

      for (const processor of preprocessors) {
        processor(cleanHTML, doc)
      }

      return doc.body
    }
  }

  /**
   * Deserialize HTML.
   *
   * @param html - The HTML to deserialize, as a string
   * @returns Array of blocks - either portable text blocks or other allowed blocks
   */
  deserialize = (html: string): TypedObject[] => {
    this._markDefs = []
    const {parseHtml} = this
    const fragment = parseHtml(html)
    const children = Array.from(fragment.childNodes) as HTMLElement[]

    const blocks = trimWhitespace(
      {schema: this.schema},
      this.whitespaceMode,
      flattenNestedBlocks(
        {schema: this.schema},
        ensureRootIsBlocks(
          this.schema,
          this.deserializeElements(children) as Array<ArbitraryTypedObject>,
        ),
      ),
    )

    if (this._markDefs.length > 0) {
      blocks
        .filter((block) => isTextBlock({schema: this.schema}, block))
        .forEach((block) => {
          block.markDefs = block.markDefs || []
          block.markDefs = block.markDefs.concat(
            this._markDefs.filter((def) => {
              return block.children
                .flatMap((child) => child.marks || [])
                .includes(def._key)
            }),
          )
        })
    }

    return blocks.map((block) => {
      if (block._type === 'block') {
        block._type = this.schema.block.name
      }
      return block
    })
  }

  /**
   * Deserialize an array of DOM elements.
   *
   * @param elements - Array of DOM elements to deserialize
   * @returns
   */
  deserializeElements = (elements: Node[] = []): TypedObject[] => {
    let nodes: TypedObject[] = []
    elements.forEach((element) => {
      nodes = nodes.concat(this.deserializeElement(element))
    })
    return nodes
  }

  /**
   * Deserialize a DOM element
   *
   * @param element - Deserialize a DOM element
   * @returns
   */
  deserializeElement = (element: Node): TypedObject | TypedObject[] => {
    const next = (
      elements: Node | Node[] | NodeList,
    ): TypedObject | TypedObject[] | undefined => {
      if (isNodeList(elements)) {
        return this.deserializeElements(Array.from(elements))
      }

      if (Array.isArray(elements)) {
        return this.deserializeElements(elements)
      }

      if (!elements) {
        return undefined
      }

      return this.deserializeElement(elements)
    }

    const block = (props: ArbitraryTypedObject) => {
      return {
        _type: '__block',
        block: props,
      }
    }

    let node: TypedObject | Array<TypedObject> | undefined
    for (let i = 0; i < this.rules.length; i++) {
      const rule = this.rules[i]
      if (!rule || !rule.deserialize) {
        continue
      }

      const ret = rule.deserialize(element, next, block)
      const type = resolveJsType(ret)

      if (
        type !== 'array' &&
        type !== 'object' &&
        type !== 'null' &&
        type !== 'undefined'
      ) {
        throw new Error(
          `A rule returned an invalid deserialized representation: "${node}".`,
        )
      }

      if (ret === undefined) {
        continue
      } else if (ret === null) {
        throw new Error('Deserializer rule returned `null`')
      } else if (Array.isArray(ret)) {
        node = ret
      } else if (isPlaceholderDecorator(ret)) {
        node = this.deserializeDecorator(ret)
      } else if (isPlaceholderAnnotation(ret)) {
        node = this.deserializeAnnotation(ret)
      } else {
        node = ret
      }

      // Set list level on list item
      if (
        ret &&
        !Array.isArray(ret) &&
        isMinimalBlock(ret) &&
        'listItem' in ret
      ) {
        let parent = element.parentNode?.parentNode
        while (parent && tagName(parent) === 'li') {
          parent = parent.parentNode?.parentNode
          ret.level = ret.level ? ret.level + 1 : 1
        }
      }

      // Set newlines on spans orginating from a block element within a blockquote
      if (
        ret &&
        !Array.isArray(ret) &&
        isMinimalBlock(ret) &&
        ret.style === 'blockquote'
      ) {
        ret.children.forEach((child, index) => {
          if (isMinimalSpan(child) && child.text === '\r') {
            child.text = '\n'
            if (index === 0 || index === ret.children.length - 1) {
              ret.children.splice(index, 1)
            }
          }
        })
      }
      break
    }

    return node || next(element.childNodes) || []
  }

  /**
   * Deserialize a `__decorator` type
   * (an internal made up type to process decorators exclusively)
   *
   * @param decorator -
   * @returns array of ...
   */
  deserializeDecorator = (decorator: PlaceholderDecorator): TypedObject[] => {
    const {name} = decorator
    const applyDecorator = (node: TypedObject) => {
      if (isPlaceholderDecorator(node)) {
        return this.deserializeDecorator(node)
      } else if (isMinimalSpan(node)) {
        node.marks = node.marks || []
        if (node.text.trim()) {
          // Only apply marks if this is an actual text
          node.marks.unshift(name)
        }
      } else if (
        'children' in node &&
        Array.isArray((node as PortableTextBlock).children)
      ) {
        const block = node as any
        block.children = block.children.map(applyDecorator)
      }
      return node
    }
    return decorator.children.reduce((children, node) => {
      const ret = applyDecorator(node)
      if (Array.isArray(ret)) {
        return children.concat(ret)
      }
      children.push(ret)
      return children
    }, [] as TypedObject[])
  }

  /**
   * Deserialize a `__annotation` object.
   * (an internal made up type to process annotations exclusively)
   *
   * @param annotation -
   * @returns Array of...
   */
  deserializeAnnotation = (
    annotation: PlaceholderAnnotation,
  ): TypedObject[] => {
    const {markDef} = annotation
    this._markDefs.push(markDef)
    const applyAnnotation = (node: TypedObject) => {
      if (isPlaceholderAnnotation(node)) {
        return this.deserializeAnnotation(node)
      } else if (isMinimalSpan(node)) {
        node.marks = node.marks || []
        if (node.text.trim()) {
          // Only apply marks if this is an actual text
          node.marks.unshift(markDef._key)
        }
      } else if (
        'children' in node &&
        Array.isArray((node as PortableTextBlock).children)
      ) {
        const block = node as any
        block.children = block.children.map(applyAnnotation)
      }
      return node
    }
    return annotation.children.reduce((children, node) => {
      const ret = applyAnnotation(node)
      if (Array.isArray(ret)) {
        return children.concat(ret)
      }
      children.push(ret)
      return children
    }, [] as TypedObject[])
  }
}
